import { Link } from "wouter";
import { formatUnits } from "viem";
import { useCurveStats } from "@/lib/curve-stats";
import { CURVE } from "@/lib/contracts";

function fmt(v: bigint | undefined, d = 0): string {
  if (v === undefined) return "—";
  return Number(formatUnits(v, 18)).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

// Compact millions form for the reserve readout, e.g. 1698641 -> "1.70M".
function millions(v: bigint | undefined): string {
  if (v === undefined) return "—";
  return (Number(formatUnits(v, 18)) / 1_000_000).toFixed(2) + "M";
}

// A live status card for the Trade view around the bond. Everything shown is
// a real on-chain read via useCurveStats (deduped by react-query): minted and
// S_MAX drive the meter, remainingToBond is S_MAX - minted, and remainingUsds
// is the contract's own quoteBuy clamp, the exact USDS of buys left. The card
// renders nothing until `bonded` has actually loaded from the chain, and
// nothing at all while the curve is far from the bond.
export function BondStatusCard() {
  const stats = useCurveStats();
  if (stats.bonded === undefined) return null;

  if (stats.bonded) {
    return (
      <aside className="panel bondcard" aria-label="Bond status">
        <div className="panel-pad">
          <div className="bond-head">
            <span className="bond-title">The curve has bonded</span>
            <span className="bond-pct mono">100% minted</span>
          </div>
          <div className="bond-track" role="img" aria-label="all sellable 01 has been minted">
            <span className="bond-fill" style={{ width: "100%" }} />
          </div>
          <p className="bond-sub">
            All <span className="mono">{fmt(stats.sMax)}</span> sellable 01 are minted and issuance
            is over, permanently. The curve now runs a permanent buyback: it only buys 01 back at
            the floor, and no new 01 will ever be made. From here, trading moves to secondary
            markets.
          </p>
          <ul className="bond-list">
            <li>
              To exit, sell 01 to the reserve at the floor here at any time, or trade on secondary
              markets where the price floats above the floor.
            </li>
            <li>
              Now that one curve price is gone, holders make the market. Pair 01 with a stablecoin
              or ETH in a pool and earn the swap fees, while the floor stays underneath it.
            </li>
            <li>
              The floor keeps rising: the reserve keeps earning yield no one can withdraw, and
              every sale still burns 0.25 percent.
            </li>
          </ul>
          <p className="bond-cta">
            <Link href="/app/info">See every 01 pool and provide liquidity →</Link>
          </p>
          <p className="bond-cta">
            <Link href="/blog/bonding">What the bond changed and how to act →</Link>
          </p>
        </div>
      </aside>
    );
  }

  if (!stats.nearBond || stats.pctIssued === undefined) return null;

  const pct = stats.pctIssued;
  const reservePct =
    stats.reserve !== undefined
      ? (Number(formatUnits(stats.reserve, 18)) / CURVE.RESERVE_AT_BOND) * 100
      : undefined;
  return (
    <aside className="panel bondcard" aria-label="Bond status">
      <div className="panel-pad">
        <div className="bond-head">
          <span className="bond-title">Approaching the bond</span>
          <span className="bond-pct mono">{pct.toFixed(1)}% there</span>
        </div>

        <div className="bond-meter">
          <div className="bond-meter-row">
            <span className="bond-meter-lab">sellable 01 minted</span>
            <span className="bond-meter-val mono">{pct.toFixed(1)}%</span>
          </div>
          <div
            className="bond-track"
            role="img"
            aria-label={`${pct.toFixed(1)} percent of the sellable supply has been minted; the bond is the end of the bar`}
          >
            <span className="bond-fill" style={{ width: Math.min(100, pct) + "%" }} />
          </div>
        </div>

        {reservePct !== undefined && (
          <div className="bond-meter">
            <div className="bond-meter-row">
              <span className="bond-meter-lab">reserve building underneath</span>
              <span className="bond-meter-val mono">{reservePct.toFixed(1)}%</span>
            </div>
            <div
              className="bond-track"
              role="img"
              aria-label={`the reserve is ${reservePct.toFixed(1)} percent of the roughly 2 million USDS it reaches by the bond`}
            >
              <span
                className="bond-fill alt"
                style={{ width: Math.min(100, reservePct) + "%" }}
              />
            </div>
            <div className="bond-meter-note mono">
              ${millions(stats.reserve)} of ~$2.0M by the bond
            </div>
          </div>
        )}

        <p className="bond-sub">
          <span className="mono">{fmt(stats.minted)}</span> of{" "}
          <span className="mono">{fmt(stats.sMax)}</span> sellable 01 minted. About{" "}
          <span className="mono">{fmt(stats.remainingToBond)}</span> 01 and{" "}
          <span className="mono">{fmt(stats.remainingUsds)}</span> USDS of buys are left until the
          curve bonds, both measured from the contract right now. The bond fires on this minted
          count, which includes burned 01, not on the reserve.
        </p>
        <ul className="bond-list">
          <li>At the bond, issuance stops forever. Buys close and no new 01 is ever made.</li>
          <li>
            Sells never close. The curve becomes a permanent buyback, so 01 can always be sold to
            the reserve at the floor.
          </li>
          <li>
            A buy larger than what remains mints only the remaining 01 and refunds the overpayment.
          </li>
          <li>
            After the bond, 01 keeps trading on secondary markets at prices above the floor.
          </li>
        </ul>
        <p className="bond-cta">
          <Link href="/blog/bonding">What the bond changes and how to act →</Link>
        </p>
      </div>
    </aside>
  );
}
